import { csvEscapeField } from '~/pages/projectDetails/potholing/csvEscapeField';
import type { UtilityAgency, UtilityContact } from '~/pages/projectDetails/utilitiesV2/common';

function filterBlankEmails(contact: UtilityContact) {
  return contact.email && contact.email.length > 0;
}

function filterStatusActive(contact: UtilityContact) {
  return contact.status && contact.status.toLowerCase() === 'active';
}

type UtilityContactWithAgency = UtilityContact & {
  agencyName: string;
  agencyType: string;
};

function sortContactByAgencyAndDefaultContact(a: UtilityContactWithAgency, b: UtilityContactWithAgency) {
  const nameCompare = a.agencyName.localeCompare(b.agencyName);
  if (nameCompare !== 0) return nameCompare;

  const aVal = a.isDefaultContact ? 1 : 0; // undefined → 0
  const bVal = b.isDefaultContact ? 1 : 0;

  // same name → sort by boolean (true first)
  return bVal - aVal;
}

function groupContactInfo(accumulator: { [key: string]: any }, contact: UtilityContactWithAgency) {
  const key = `${contact.agencyName}-${contact.agencyType}`;
  if (!accumulator[key]) {
    accumulator[key] = { ...contact, contacts: [] };
  }
  const name = contact.name ? contact.name : contact.email;
  accumulator[key].contacts.push({
    name,
    email: contact.email,
  });

  return accumulator;
}

type AgencyContactEmail = { email: string; name?: string; agencyIds: string[] };

export interface AgencyContactCard {
  agencyName: string;
  assetTypes: string[];
  // agencyIds are the agency rows that list this email, for analytics
  emails: AgencyContactEmail[];
}

const compareNames = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

const compareCards = (a: AgencyContactCard, b: AgencyContactCard) =>
  compareNames(a.assetTypes[0] ?? '', b.assetTypes[0] ?? '') || compareNames(a.agencyName, b.agencyName);

/**
 * Groups the project's agencies into one Contacts modal card per agency name, so an agency with several asset
 * types gets one card listing every type. Agencies with no active email come back separately for the
 * `No contact on file` card. Both lists are ordered by first asset type, then name. The API cleans the emails
 * (one lowercase address per contact), so a contact with a blank email doesn't count.
 */
export function getAgencyContactCards(agencies: { [key: string]: UtilityAgency }): {
  withContacts: AgencyContactCard[];
  withoutContacts: AgencyContactCard[];
} {
  const cards = new Map<string, AgencyContactCard>();
  const defaultEmails = new Set<AgencyContactEmail>();
  for (const agency of Object.values(agencies)) {
    // Request-only rows are no longer on the project's agency list (CG-1219).
    if (agency.isRequestOnly) continue;
    const card = cards.get(agency.agencyName) ?? { agencyName: agency.agencyName, assetTypes: [], emails: [] };
    cards.set(agency.agencyName, card);
    if (agency.type && !card.assetTypes.includes(agency.type)) card.assetTypes.push(agency.type);
    for (const contact of (agency.contacts ?? []).filter(filterStatusActive)) {
      if (!contact.email) continue;
      let entry = card.emails.find((e) => e.email === contact.email);
      if (!entry) {
        entry = { email: contact.email, agencyIds: [] };
        card.emails.push(entry);
      }
      if (!entry.name && contact.name) entry.name = contact.name;
      if (!entry.agencyIds.includes(agency.id)) entry.agencyIds.push(agency.id);
      if (contact.isDefaultContact) defaultEmails.add(entry);
    }
  }

  const all = [...cards.values()];
  for (const card of all) {
    card.assetTypes.sort(compareNames);
    card.emails.sort((a, b) => Number(defaultEmails.has(b)) - Number(defaultEmails.has(a)));
  }
  all.sort(compareCards);
  const withContacts = all.filter((card) => card.emails.length > 0);
  const withoutContacts = all.filter((card) => card.emails.length === 0);
  return { withContacts, withoutContacts };
}

/** Every asset type across the project's agencies, for the Contacts modal's Type filter. */
export function getAgencyAssetTypes(cards: AgencyContactCard[]): string[] {
  return [...new Set(cards.flatMap((card) => card.assetTypes))].sort(compareNames);
}

/**
 * Narrows the Contacts modal cards to the search text and picked asset types. Search keeps the emails whose agency,
 * name, or email contains the text in any case, and the `No contact on file` agencies whose name contains it. Picking types keeps
 * agencies with at least one of them, leads each card's badges with its first picked type, and sorts by that type;
 * with none picked, every agency stays.
 */
export function filterAgencyContactCards(
  { withContacts, withoutContacts }: { withContacts: AgencyContactCard[]; withoutContacts: AgencyContactCard[] },
  search: string,
  assetTypes: string[],
): { withContacts: AgencyContactCard[]; withoutContacts: AgencyContactCard[] } {
  const query = search.trim().toLowerCase();
  const hasType = (card: AgencyContactCard) =>
    assetTypes.length === 0 || card.assetTypes.some((type) => assetTypes.includes(type));
  const matches = (card: AgencyContactCard, email: AgencyContactCard['emails'][number]) =>
    [card.agencyName, email.name ?? '', email.email].some((text) => text.toLowerCase().includes(query));
  const leadPickedType = (cards: AgencyContactCard[]) => {
    if (assetTypes.length === 0) return cards;
    return cards
      .map((card) => {
        const lead = card.assetTypes.find((type) => assetTypes.includes(type));
        return lead === card.assetTypes[0] || !lead
          ? card
          : { ...card, assetTypes: [lead, ...card.assetTypes.filter((type) => type !== lead)] };
      })
      .sort(compareCards);
  };
  return {
    withContacts: leadPickedType(
      withContacts
        .filter(hasType)
        .map((card) => (query ? { ...card, emails: card.emails.filter((email) => matches(card, email)) } : card))
        .filter((card) => card.emails.length > 0),
    ),
    withoutContacts: leadPickedType(
      withoutContacts.filter((card) => hasType(card) && card.agencyName.toLowerCase().includes(query)),
    ),
  };
}

// A spreadsheet reads a cell starting with one of these as a formula, including a leading tab or line break and the
// full-width forms. Contacts can come from the web (AI contact research), so a leading `'` keeps them as text.
const FORMULA_START = /^[=+\-@\t\r\n＝＋－＠]/;

// Quotes a value with a tab too, so a spreadsheet that splits on tabs keeps it in one cell.
const csvContactField = (value: string) => {
  const text = FORMULA_START.test(value) ? `'${value}` : value;
  return text.includes('\t') ? `"${text.replaceAll('"', '""')}"` : csvEscapeField(text);
};

/** The Contacts modal's `Download CSV` file: one row per email on the given cards, in card order. */
export function getAgencyContactsCsv(cards: AgencyContactCard[]): string {
  const rows = [
    ['Agency', 'Asset type', 'Contact name', 'Email'],
    ...cards.flatMap((card) =>
      card.emails.map(({ email, name }) => [card.agencyName, card.assetTypes.join(', '), name ?? '', email]),
    ),
  ];
  return rows.map((row) => `${row.map(csvContactField).join(',')}\n`).join('');
}

/**
 * Retrieves and formats utility contact information from the provided agencies.
 *
 * @param {Object.<string, UtilityAgency>} agencies - A dictionary of utility agencies indexed by their IDs.
 * Each agency contains contact information and agency details.
 *
 * @return {Array.<UtilityContactWithAgency>} An array of formatted contact information objects.
 * Each object contains aggregated agency and contact details such as agency name,
 * agency type, and a comma-separated string of contact details.
 */
export function getUtilityContacts(agencies: { [key: string]: UtilityAgency }): UtilityContactWithAgency[] {
  return Object.values(
    Object.values(agencies)
      .flatMap((agency) => {
        const contacts = agency.contacts ?? [];
        return contacts
          .filter(filterBlankEmails)
          .filter(filterStatusActive)
          .map((contact) => ({
            ...contact,
            agencyName: agency.agencyName,
            agencyType: agency.type,
          }))
          .sort(sortContactByAgencyAndDefaultContact);
      })
      .reduce(groupContactInfo, {}),
  );
}
